import { db } from '../../db/indexeddb'
import {
  getCloudProjectComments,
  getCloudProjectCommentIds,
  getCloudProjectCommentsByIds,
  getDeletedCloudProjectComments,
  createCloudProjectComment,
  updateCloudProjectComment,
  deleteCloudProjectComment,
} from '../../supabase/queries'
import { CloudProjectCommentSchema, CloudProjectCommentPayloadSchema } from '../sync-schemas'
import { resolveCloudProjectId, resolveLocalProjectId } from '../sync-resolve'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { ProjectComment } from '../../db/models'

/* ── PROJECT COMMENTS (Kommentare an Projekten, #184) ─────────────────── */

/** Die lokale Kennung des Projekts — erst über `project_local_id`, dann über die Cloud-Kennung. */
async function localProjectOf(remote: { project_id: string; project_local_id?: string | null }): Promise<string | null> {
  if (remote.project_local_id) {
    const byLocal = await db.projects.get(remote.project_local_id)
    if (byLocal) return byLocal.id
  }
  return resolveLocalProjectId(remote.project_id)
}

export const projectCommentPullDescriptor: PullDescriptor<any> = {
  // `entityName + 's'` ist der Name in den Log-Zeilen; die Warteschlange heißt wie die Tabelle.
  entityName: 'projectComment',
  settingsKey: 'lastProjectCommentSyncTime',

  fetchRemote: (userId, since) => getCloudProjectComments(userId, since),
  fetchRemoteIds: (userId) => getCloudProjectCommentIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudProjectCommentsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudProjectComments(userId, since),

  safeParse: (raw) => CloudProjectCommentSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.projectComments,
  getAllLocal: () => db.projectComments.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.projectComments.filter(c => !c.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote) => {
    const projectId = await localProjectOf(remote)
    // Das Projekt ist noch nicht auf diesem Gerät — der Projekt-Pull läuft vorher und legt es an;
    // fehlt es trotzdem, holt der nächste Zyklus den Kommentar nach.
    if (!projectId) return null
    return {
      id: crypto.randomUUID(),
      projectId,
      content: remote.content,
      postedAt: new Date(remote.posted_at),
      importedFrom: remote.imported_from ?? null,
      userId: remote.user_id,
      cloudId: remote.id,
      deletedAt: null,
      createdAt: new Date(remote.created_at),
      updatedAt: new Date(remote.updated_at),
      lastModified: new Date(remote.updated_at),
      synced: true,
      syncedAt: new Date(),
    }
  },

  buildLocalUpdate: async (remote) => ({
    content: remote.content,
    postedAt: new Date(remote.posted_at),
    ...('imported_from' in remote ? { importedFrom: remote.imported_from ?? null } : {}),
    updatedAt: new Date(remote.updated_at),
    deletedAt: remote.deleted_at ? new Date(remote.deleted_at) : null,
  }),
}

export const projectCommentPushDescriptor: PushDescriptor<ProjectComment> = {
  entityName: 'CloudProjectComment',
  getLocal: async (id) => (await db.projectComments.get(id)) ?? null,
  getLocalCloudId: (l) => l.cloudId,
  getLocalId: (l) => l.id,
  table: db.projectComments,

  // Ein Kommentar kann nicht vor seinem Projekt in der Cloud stehen — er wartet auf dessen Kennung.
  canCreate: async (comment) => {
    const cloudProjectId = await resolveCloudProjectId(comment.projectId)
    return cloudProjectId ? { defer: false } : { defer: true }
  },

  canUpdate: async (comment) => {
    const cloudProjectId = await resolveCloudProjectId(comment.projectId)
    return cloudProjectId ? { defer: false } : { defer: true }
  },

  buildCreatePayload: async (comment) => payloadOf(comment),

  buildUpdatePayload: async (comment) => {
    const payload: Record<string, any> = await payloadOf(comment)
    // Das Rückgängig einer Löschung belebt die Zeile wieder — die `null` muss
    // ausdrücklich mit, sonst bleibt sie oben tot (Sync-Checkliste Punkt 17).
    if (!comment.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudProjectComment(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudProjectComment(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudProjectComment(cloudId, userId),
}

/** Alle Felder, immer — `content` trägt das Ändern. */
async function payloadOf(comment: ProjectComment) {
  const cloudProjectId = await resolveCloudProjectId(comment.projectId)
  return CloudProjectCommentPayloadSchema.parse({
    project_id: cloudProjectId,
    project_local_id: comment.projectId,
    content: comment.content,
    posted_at: comment.postedAt.toISOString(),
    imported_from: comment.importedFrom ?? null,
    local_id: comment.id,
  })
}
